import { useRef, useState } from "react";
import { motion, useScroll, useMotionValueEvent, useSpring } from "framer-motion";
import { Reveal, SectionLabel, MaskLines } from "./Reveal";
import { PROCESS_VISUALS } from "./ProcessVisuals";
import { useContent } from "../../lib/content";

const VisualPanel = ({ index }) => {
  const V = PROCESS_VISUALS[index] || PROCESS_VISUALS[0];
  return (
    <div className="glass rounded-3xl p-5 sm:p-6 h-full relative overflow-hidden">
      <div className="flex items-center justify-between mb-5">
        <div className="flex gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-white/10" /><span className="w-2.5 h-2.5 rounded-full bg-white/10" /><span className="w-2.5 h-2.5 rounded-full bg-white/10" /></div>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Fase 0{index + 1} · esempio grafico</span>
      </div>
      <div className="min-h-[300px]"><V /></div>
    </div>
  );
};

export const Process = () => {
  const h = useContent().home;
  const steps = Array.isArray(h.process_steps) ? h.process_steps.slice(0, 4) : [];
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 60%", "end 60%"] });
  const fill = useSpring(scrollYProgress, { stiffness: 80, damping: 20 });
  useMotionValueEvent(scrollYProgress, "change", (v) => setActive(Math.max(0, Math.min(steps.length - 1, Math.floor(v * steps.length)))));

  return (
    <section id="come-funziona" data-testid="process-section" className="relative py-24 sm:py-32 bg-[#070c18] border-y hairline">
      <div className="absolute inset-0 grid-bg opacity-60 [mask-image:linear-gradient(to_bottom,transparent,black_20%,black_80%,transparent)]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal><SectionLabel index="02">{h.process_label}</SectionLabel></Reveal>
        <h2 className="mt-7 max-w-4xl font-display font-extrabold text-white text-3xl sm:text-5xl lg:text-6xl leading-[1.02]">
          <MaskLines text={h.process_title.replace(". ", ".\n")} lineClass={(i) => (i === 1 ? "text-frost-grad" : "")} />
        </h2>
        <div ref={ref} className="mt-16 lg:mt-24 grid lg:grid-cols-2 gap-10 lg:gap-16">
          <div className="hidden lg:block">
            <div className="sticky top-28 h-[440px]">
              <VisualPanel index={active} />
            </div>
          </div>
          <div className="relative pl-10 sm:pl-14">
            <div className="absolute left-[11px] sm:left-[15px] top-2 bottom-2 w-px bg-white/10" />
            <motion.div style={{ scaleY: fill }} className="absolute left-[11px] sm:left-[15px] top-2 bottom-2 w-px origin-top bg-gradient-to-b from-[#4fc3f7] via-[#ff5500] to-[#ff5500]" />
            {steps.map((s, i) => (
              <div key={i} data-testid={`process-step-${i}`} className="relative lg:min-h-[58vh] pb-14 lg:pb-0 lg:flex lg:flex-col lg:justify-center">
                <span className={`absolute -left-10 sm:-left-14 top-1 lg:top-1/2 lg:-translate-y-1/2 w-6 h-6 sm:w-8 sm:h-8 grid place-items-center rounded-full border font-mono text-[10px] sm:text-xs transition-colors duration-500 ${i <= active ? "bg-[#ff5500] border-[#ff5500] text-white" : "bg-[#070c18] border-white/20 text-slate-400"}`}>{i + 1}</span>
                <Reveal>
                  <p className="font-display italic font-black text-6xl sm:text-7xl text-outline leading-none">0{i + 1}</p>
                  <h3 className="mt-3 font-display-n font-bold text-white text-2xl sm:text-3xl leading-tight">{s.title}</h3>
                  <p className="mt-4 text-slate-300 leading-relaxed max-w-md">{s.text}</p>
                  {i === 3 && <p className="mt-4 inline-block font-mono text-[11px] uppercase tracking-[0.18em] text-[#ff8a4c]">A cura della tua azienda</p>}
                </Reveal>
                <div className="lg:hidden mt-6"><VisualPanel index={i} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
